import React, { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { makeApiCall, makeJsonApiCall } from '../utils/customerUtils'
import { useWebSocket } from '../contexts/WebSocketContext'
import {
  MessageSquare,
  Send,
  Clock,
  User,
  Mail,
  Phone,
  CheckCheck,
  X,
  Search,
  Filter,
  AlertCircle,
  Loader
} from 'lucide-react'

interface ChatSession {
  id: number
  session_id: string
  status: 'waiting' | 'active' | 'closed'
  visitor_name: string
  visitor_email: string
  visitor_phone?: string
  visitor_data?: any
  reason?: string
  assigned_to?: number
  assigned_to_name?: string
  started_at: string
  closed_at?: string
  unread_count: number
  customer_username?: string
  subscription_status?: string
}

interface ChatMessage {
  id: number
  session_id: number
  message: string
  sender_type: 'visitor' | 'agent' | 'system'
  sender_name?: string
  sender_id?: number
  agent_name?: string
  is_internal: boolean
  created_at: string
  read_at?: string
}

const LiveChat: React.FC = () => {
  const { t } = useTranslation()
  const { socket } = useWebSocket()
  const [sessions, setSessions] = useState<ChatSession[]>([])
  const [selectedSession, setSelectedSession] = useState<ChatSession | null>(null)
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [messageInput, setMessageInput] = useState('')
  const [internalNote, setInternalNote] = useState('')
  const [loading, setLoading] = useState(true)
  const [sending, setSending] = useState(false)
  const [filter, setFilter] = useState<'all' | 'waiting' | 'active' | 'closed'>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const messagesEndRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    fetchSessions()
    
    // Join the agents room
    if (socket) {
      socket.emit('chat:join_agents')
    }
  }, [socket])

  useEffect(() => {
    if (!socket) return

    // Listen for new chat sessions
    socket.on('chat:new_session', (data: any) => {
      alert(`New chat request from ${data.visitor_name}`)
      fetchSessions()
    })

    // Listen for new messages
    socket.on('chat:message', (data: any) => {
      if (selectedSession && data.session_id === selectedSession.session_id) {
        setMessages(prev => [...prev, data])
        scrollToBottom()
      }
      // Update unread count
      fetchSessions()
    })

    // Listen for session updates
    socket.on('chat:session_updated', () => {
      fetchSessions()
    })

    socket.on('chat:status_change', (data: any) => {
      if (selectedSession && data.session_id === selectedSession.session_id) {
        setSelectedSession(prev => prev ? { ...prev, status: data.status } : null)
      }
      fetchSessions()
    })

    return () => {
      socket.off('chat:new_session')
      socket.off('chat:message')
      socket.off('chat:session_updated')
      socket.off('chat:status_change')
    }
  }, [socket, selectedSession])

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  const fetchSessions = async () => {
    try {
      const params = filter !== 'all' ? `?status=${filter}` : ''
      const response = await makeApiCall(`/api/chat/sessions${params}`)
      const data = await response.json()
      setSessions(data.sessions || [])
    } catch (error) {
      console.error('Error fetching support tickets:', error)
      alert('Failed to load support tickets')
    } finally {
      setLoading(false)
    }
  }

  const loadSession = async (session: ChatSession) => {
    try {
      const response = await makeApiCall(`/api/chat/sessions/${session.session_id}`)
      const data = await response.json()
      setSelectedSession(data.session)
      setMessages(data.messages || [])
      
      // Join the session room
      if (socket) {
        socket.emit('chat:join_session', { sessionId: session.session_id })
      }

      // Mark messages as read
      await makeJsonApiCall(`/api/chat/sessions/${session.session_id}/mark-read`, 'PUT', {})
      fetchSessions() // Refresh to update unread counts
    } catch (error) {
      console.error('Error loading ticket:', error)
      alert('Failed to load support ticket')
    }
  }

  const acceptChat = async () => {
    if (!selectedSession) return
    
    try {
      await makeJsonApiCall(`/api/chat/sessions/${selectedSession.session_id}/accept`, 'PUT', {})
      alert('Support ticket accepted')
      setSelectedSession({ ...selectedSession, status: 'active' })
      fetchSessions()
    } catch (error) {
      console.error('Error accepting ticket:', error)
      alert('Failed to accept support ticket')
    }
  }

  const sendMessage = async () => {
    if (!selectedSession || !messageInput.trim()) return

    try {
      setSending(true)
      await makeJsonApiCall(
        `/api/chat/sessions/${selectedSession.session_id}/agent-message`,
        'POST',
        { message: messageInput, is_internal: false }
      )
      setMessageInput('')
    } catch (error) {
      console.error('Error sending message:', error)
      alert('Failed to send message')
    } finally {
      setSending(false)
    }
  }

  const sendInternalNote = async () => {
    if (!selectedSession || !internalNote.trim()) return

    try {
      await makeJsonApiCall(
        `/api/chat/sessions/${selectedSession.session_id}/agent-message`,
        'POST',
        { message: internalNote, is_internal: true }
      )
      setInternalNote('')
      alert('Internal note added')
    } catch (error) {
      console.error('Error sending internal note:', error)
      alert('Failed to add internal note')
    }
  }

  const closeChat = async () => {
    if (!selectedSession) return

    try {
      await makeJsonApiCall(`/api/chat/sessions/${selectedSession.session_id}/close`, 'PUT', {})
      alert('Support ticket closed')
      setSelectedSession({ ...selectedSession, status: 'closed' })
      fetchSessions()
    } catch (error) {
      console.error('Error closing ticket:', error)
      alert('Failed to close support ticket')
    }
  }

  const filteredSessions = sessions.filter(session => {
    const matchesSearch = 
      session.visitor_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      session.visitor_email.toLowerCase().includes(searchQuery.toLowerCase())
    return matchesSearch
  })

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'waiting': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
      case 'active': return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
      case 'closed': return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const formatTime = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    
    if (diffMins < 1) return 'Just now'
    if (diffMins < 60) return `${diffMins}m ago`
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`
    return date.toLocaleDateString()
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    )
  }

  return (
    <div className="flex h-[calc(100vh-4rem)] bg-gray-50 dark:bg-gray-900">
      {/* Sidebar - Support Tickets */}
      <div className="w-80 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <h1 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <MessageSquare className="w-6 h-6 text-blue-600" />
            Support Tickets
          </h1>
          
          {/* Search */}
          <div className="mt-3 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search tickets..."
              className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
            />
          </div>

          {/* Filter Tabs */}
          <div className="mt-3 flex gap-2">
            {(['all', 'waiting', 'active', 'closed'] as const).map(f => (
              <button
                key={f}
                onClick={() => { setFilter(f); fetchSessions() }}
                className={`px-3 py-1 text-xs font-medium rounded-lg capitalize ${
                  filter === f
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                {f}
              </button>
            ))}
          </div>
        </div>

        {/* Sessions List */}
        <div className="flex-1 overflow-y-auto">
          {filteredSessions.length === 0 ? (
            <div className="p-4 text-center text-gray-500 dark:text-gray-400">
              <AlertCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No support tickets found</p>
            </div>
          ) : (
            filteredSessions.map(session => (
              <button
                key={session.id}
                onClick={() => loadSession(session)}
                className={`w-full p-4 border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 text-left ${
                  selectedSession?.id === session.id ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                }`}
              >
                <div className="flex items-start justify-between mb-1">
                  <div className="font-medium text-gray-900 dark:text-white">
                    {session.visitor_name}
                  </div>
                  {session.unread_count > 0 && (
                    <span className="px-2 py-0.5 bg-blue-600 text-white text-xs rounded-full">
                      {session.unread_count}
                    </span>
                  )}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                  {session.visitor_email}
                </div>
                <div className="flex items-center justify-between">
                  <span className={`px-2 py-0.5 text-xs rounded-full ${getStatusColor(session.status)}`}>
                    {session.status}
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {formatTime(session.started_at)}
                  </span>
                </div>
                {session.reason && (
                  <div className="mt-2 text-xs text-gray-500 dark:text-gray-400 italic">
                    {session.reason}
                  </div>
                )}
              </button>
            ))
          )}
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col">
        {selectedSession ? (
          <>
            {/* Ticket Header */}
            <div className="p-4 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {selectedSession.visitor_name}
                  </h2>
                  <div className="flex items-center gap-4 mt-1 text-sm text-gray-600 dark:text-gray-400">
                    <span className="flex items-center gap-1">
                      <Mail className="w-4 h-4" />
                      {selectedSession.visitor_email}
                    </span>
                    {selectedSession.visitor_phone && (
                      <span className="flex items-center gap-1">
                        <Phone className="w-4 h-4" />
                        {selectedSession.visitor_phone}
                      </span>
                    )}
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {formatTime(selectedSession.started_at)}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`px-3 py-1 text-sm rounded-full ${getStatusColor(selectedSession.status)}`}>
                    {selectedSession.status}
                  </span>
                  {selectedSession.status === 'waiting' && (
                    <button
                      onClick={acceptChat}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                    >
                      Accept Ticket
                    </button>
                  )}
                  {selectedSession.status === 'active' && (
                    <button
                      onClick={closeChat}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                    >
                      Close Ticket
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50 dark:bg-gray-900">
              {messages.map(message => (
                <div
                  key={message.id}
                  className={`flex ${
                    message.sender_type === 'agent' ? 'justify-end' : 'justify-start'
                  }`}
                >
                  <div
                    className={`max-w-[70%] rounded-lg p-3 ${
                      message.is_internal
                        ? 'bg-yellow-100 dark:bg-yellow-900/30 border border-yellow-300 dark:border-yellow-700'
                        : message.sender_type === 'agent'
                        ? 'bg-blue-600 text-white'
                        : message.sender_type === 'system'
                        ? 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400 text-center italic'
                        : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700'
                    }`}
                  >
                    {message.is_internal && (
                      <div className="text-xs font-medium text-yellow-800 dark:text-yellow-300 mb-1">
                        Internal Note
                      </div>
                    )}
                    {message.sender_type !== 'system' && message.sender_type !== 'agent' && (
                      <div className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                        {message.sender_name || 'Broker'}
                      </div>
                    )}
                    {message.sender_type === 'agent' && message.agent_name && (
                      <div className="text-xs text-blue-100 mb-1">
                        {message.agent_name}
                      </div>
                    )}
                    <div className={message.sender_type === 'agent' && !message.is_internal ? 'text-white' : 'text-gray-900 dark:text-white'}>
                      {message.message}
                    </div>
                    <div className={`text-xs mt-1 ${
                      message.sender_type === 'agent' && !message.is_internal
                        ? 'text-blue-100'
                        : 'text-gray-500 dark:text-gray-400'
                    }`}>
                      {new Date(message.created_at).toLocaleTimeString()}
                      {message.read_at && message.sender_type === 'agent' && (
                        <CheckCheck className="inline w-3 h-3 ml-1" />
                      )}
                    </div>
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>

            {/* Message Input */}
            {selectedSession.status !== 'closed' && (
              <div className="p-4 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
                {/* Internal Note Input */}
                <div className="mb-3">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={internalNote}
                      onChange={(e) => setInternalNote(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && sendInternalNote()}
                      placeholder="Add internal note (not visible to broker)..."
                      className="flex-1 px-3 py-2 border border-yellow-300 dark:border-yellow-700 rounded-lg bg-yellow-50 dark:bg-yellow-900/20 text-gray-900 dark:text-white text-sm"
                    />
                    <button
                      onClick={sendInternalNote}
                      disabled={!internalNote.trim()}
                      className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 disabled:opacity-50 text-sm"
                    >
                      Add Note
                    </button>
                  </div>
                </div>

                {/* Regular Message Input */}
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={messageInput}
                    onChange={(e) => setMessageInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                    placeholder="Type your message..."
                    className="flex-1 px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    disabled={sending}
                  />
                  <button
                    onClick={sendMessage}
                    disabled={!messageInput.trim() || sending}
                    className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                  >
                    {sending ? <Loader className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-gray-500 dark:text-gray-400">
            <div className="text-center">
              <MessageSquare className="w-16 h-16 mx-auto mb-4 opacity-20" />
              <p className="text-lg font-medium">Select a ticket to view</p>
              <p className="text-sm mt-2">Choose a support ticket from the sidebar</p>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default LiveChat

